import React, { useMemo } from 'react';
import { PageFooterNested } from './PageFooterNested';
import { FooterLink, SocialLink, ActionItem, LinkColumn, BreadcrumbItem, FooterLanguage, FooterCountry, PageFooterLogo } from './types';
import './PageFooter.scss';

/**
 * @polySemanticUsage About this block: the persistent global footer at the bottom of every page.
 * Contains secondary links (legal, privacy, terms), sitemap-style link groups, social media
 * links, and locale/language selectors. Appears once per page.
 */
export interface PageFooterProps extends React.HTMLAttributes<HTMLElement> {

  /** Color theme: 'dark' (default) = dark background with light text, 'light' = white background with dark text */
  theme?: 'dark' | 'light';

  /** Top Bar CTA (e.g. "Just have a few items to sell?") */
  /** Heading text displayed in the top bar */
  topBarText?: string;
  /** Link text displayed below or beside the heading */
  topBarLinkText?: string;
  /** URL for the top bar link */
  topBarLinkUrl?: string;
  /** CTA tracking ID for the top bar link */
  topBarCtaId?: string;

  /** Logo */
  /**
   * Logo preset name or custom image URL. Use 'amazon' for the built-in Amazon logo (auto-swaps for theme).
   * @polyType Text
   * @polyLabel Logo
   * @polyHelp Type amazon for the built-in Amazon logo (switches with the light or dark theme), or paste an image URL for a custom logo. Leave blank for no logo.
   * @polyTranslatable false
   */
  logoImageUrl?: PageFooterLogo;
  /** Logo alt text */
  logoAlt?: string;
  /** URL the logo links to */
  logoUrl?: string;
  /** CTA tracking ID for the logo link */
  logoCtaId?: string;
  /** LD tracking appendix for the logo link */
  logoLdAppendix?: string;

  /** Link Row 1 (up to 8 links) */
  /** Accessible label for the first link row navigation */
  row1AriaLabel?: string;
  row1Link1Text?: string; row1Link1Url?: string; row1Link1CtaId?: string;
  row1Link2Text?: string; row1Link2Url?: string; row1Link2CtaId?: string;
  row1Link3Text?: string; row1Link3Url?: string; row1Link3CtaId?: string;
  row1Link4Text?: string; row1Link4Url?: string; row1Link4CtaId?: string;
  row1Link5Text?: string; row1Link5Url?: string; row1Link5CtaId?: string;
  row1Link6Text?: string; row1Link6Url?: string; row1Link6CtaId?: string;
  row1Link7Text?: string; row1Link7Url?: string; row1Link7CtaId?: string;
  row1Link8Text?: string; row1Link8Url?: string; row1Link8CtaId?: string;

  /** Link Row 2 (up to 8 links) */
  /** Accessible label for the second link row navigation */
  row2AriaLabel?: string;
  row2Link1Text?: string; row2Link1Url?: string; row2Link1CtaId?: string;
  row2Link2Text?: string; row2Link2Url?: string; row2Link2CtaId?: string;
  row2Link3Text?: string; row2Link3Url?: string; row2Link3CtaId?: string;
  row2Link4Text?: string; row2Link4Url?: string; row2Link4CtaId?: string;
  row2Link5Text?: string; row2Link5Url?: string; row2Link5CtaId?: string;
  row2Link6Text?: string; row2Link6Url?: string; row2Link6CtaId?: string;
  row2Link7Text?: string; row2Link7Url?: string; row2Link7CtaId?: string;
  row2Link8Text?: string; row2Link8Url?: string; row2Link8CtaId?: string;

  // --- Social Links (up to 6) ---
  /** Social links require all three of url, iconUrl, and iconAlt to render. Links missing iconAlt are silently skipped for accessibility. */
  social1Url?: string; social1IconUrl?: string; social1IconAlt?: string; social1CtaId?: string;
  social2Url?: string; social2IconUrl?: string; social2IconAlt?: string; social2CtaId?: string;
  social3Url?: string; social3IconUrl?: string; social3IconAlt?: string; social3CtaId?: string;
  social4Url?: string; social4IconUrl?: string; social4IconAlt?: string; social4CtaId?: string;
  social5Url?: string; social5IconUrl?: string; social5IconAlt?: string; social5CtaId?: string;
  social6Url?: string; social6IconUrl?: string; social6IconAlt?: string; social6CtaId?: string;

  /** Link Columns (up to 5 columns, 6 links each) */
  col1Title?: string;
  col1Link1Text?: string; col1Link1Url?: string; col1Link1CtaId?: string;
  col1Link2Text?: string; col1Link2Url?: string; col1Link2CtaId?: string;
  col1Link3Text?: string; col1Link3Url?: string; col1Link3CtaId?: string;
  col1Link4Text?: string; col1Link4Url?: string; col1Link4CtaId?: string;
  col1Link5Text?: string; col1Link5Url?: string; col1Link5CtaId?: string;
  col1Link6Text?: string; col1Link6Url?: string; col1Link6CtaId?: string;
  col2Title?: string;
  col2Link1Text?: string; col2Link1Url?: string; col2Link1CtaId?: string;
  col2Link2Text?: string; col2Link2Url?: string; col2Link2CtaId?: string;
  col2Link3Text?: string; col2Link3Url?: string; col2Link3CtaId?: string;
  col2Link4Text?: string; col2Link4Url?: string; col2Link4CtaId?: string;
  col2Link5Text?: string; col2Link5Url?: string; col2Link5CtaId?: string;
  col2Link6Text?: string; col2Link6Url?: string; col2Link6CtaId?: string;
  col3Title?: string;
  col3Link1Text?: string; col3Link1Url?: string; col3Link1CtaId?: string;
  col3Link2Text?: string; col3Link2Url?: string; col3Link2CtaId?: string;
  col3Link3Text?: string; col3Link3Url?: string; col3Link3CtaId?: string;
  col3Link4Text?: string; col3Link4Url?: string; col3Link4CtaId?: string;
  col3Link5Text?: string; col3Link5Url?: string; col3Link5CtaId?: string;
  col3Link6Text?: string; col3Link6Url?: string; col3Link6CtaId?: string;
  col4Title?: string;
  col4Link1Text?: string; col4Link1Url?: string; col4Link1CtaId?: string;
  col4Link2Text?: string; col4Link2Url?: string; col4Link2CtaId?: string;
  col4Link3Text?: string; col4Link3Url?: string; col4Link3CtaId?: string;
  col4Link4Text?: string; col4Link4Url?: string; col4Link4CtaId?: string;
  col4Link5Text?: string; col4Link5Url?: string; col4Link5CtaId?: string;
  col4Link6Text?: string; col4Link6Url?: string; col4Link6CtaId?: string;
  col5Title?: string;
  col5Link1Text?: string; col5Link1Url?: string; col5Link1CtaId?: string;
  col5Link2Text?: string; col5Link2Url?: string; col5Link2CtaId?: string;
  col5Link3Text?: string; col5Link3Url?: string; col5Link3CtaId?: string;
  col5Link4Text?: string; col5Link4Url?: string; col5Link4CtaId?: string;
  col5Link5Text?: string; col5Link5Url?: string; col5Link5CtaId?: string;
  col5Link6Text?: string; col5Link6Url?: string; col5Link6CtaId?: string;

  // --- Meta Action Items (Language & Country selectors) ---
  /**
   * Action slots map to picker popups: slot 1 → language picker, slot 2 → country picker.
   * For general footer links (e.g. Feedback), use row1Link props instead.
   */
  action1IconUrl?: string; action1IconAlt?: string; action1Label?: string; action1OnClick?: () => void;
  action2IconUrl?: string; action2IconAlt?: string; action2Label?: string; action2OnClick?: () => void;
  /** Sprite background-position Y offset (px, positive value). Used with flag sprite sheets. */
  action2SpriteY?: number;

  /** Copyright, Disclaimer & Footnotes */
  /** Copyright text displayed in the bottom bar (e.g. '© 2026, Amazon.com Services LLC.') */
  copyrightText?: string;
  /** Disclaimer text rendered as Markdown above the action buttons */
  disclaimerText?: string;
  /** Footnotes text rendered as Markdown at the top of the footer (above disclaimer). Supports anchor IDs for scroll-to. */
  footnotesText?: string;

  /** Breadcrumbs */
  /** JSON string of Array<{ text: string; url?: string }> for breadcrumb trail */
  breadcrumbs?: string;

  /** Screen-reader names for the footer navigation areas */
  /**
   * Accessible name for the link-column navigation.
   * @polyLabel Site Map Screen Reader Label
   * @polyHelp Name screen readers announce for the link columns. Translated with the page.
   * @polyDefault Footer site map
   */
  siteMapAriaLabel?: string;
  /**
   * Accessible name for the social media links.
   * @polyLabel Social Links Screen Reader Label
   * @polyHelp Name screen readers announce for the social media links. Translated with the page.
   * @polyDefault Social media
   */
  socialAriaLabel?: string;
  /**
   * Accessible name for the breadcrumb trail.
   * @polyLabel Breadcrumb Screen Reader Label
   * @polyHelp Name screen readers announce for the breadcrumb trail. Translated with the page.
   * @polyDefault Breadcrumb
   */
  breadcrumbAriaLabel?: string;

  /** Language Picker Popup */
  /** JSON string of Array<{ code: string; name: string }> for available languages */
  languages?: string;
  /** Current language code (e.g. 'en-US') */
  currentLanguage?: string;
  /** Language picker popup heading */
  languagePickerHeading?: string;
  /** Append locale to URL path (e.g. /en/page) */
  languagePickerAddPathLocale?: boolean;

  /** Country Picker Popup */
  /** JSON string of Array<{ code: string; name: string; url: string; spriteY: number }> */
  countries?: string;
  /** Current country code (e.g. 'US') */
  currentCountry?: string;
  /** Country picker popup heading */
  countryPickerHeading?: string;
  /** Country picker footnote text */
  countryPickerFootnote?: string;
  /** Flag sprite sheet URL */
  countrySpriteUrl?: string;
}

// --- Internal helpers: collect flat numbered props into structured arrays ---

function collectLinks(props: Record<string, unknown>, row: 1 | 2): FooterLink[] {
  const links: FooterLink[] = [];
  for (let i = 1; i <= 8; i++) {
    const text = props[`row${row}Link${i}Text`] as string | undefined;
    const url = props[`row${row}Link${i}Url`] as string | undefined;
    const ctaId = props[`row${row}Link${i}CtaId`] as string | undefined;
    if (text && url) links.push({ text, url, ctaId });
  }
  return links;
}

function collectSocialLinks(props: Record<string, unknown>): SocialLink[] {
  const links: SocialLink[] = [];
  for (let i = 1; i <= 6; i++) {
    const url = props[`social${i}Url`] as string | undefined;
    const iconUrl = props[`social${i}IconUrl`] as string | undefined;
    const iconAlt = props[`social${i}IconAlt`] as string | undefined;
    const ctaId = props[`social${i}CtaId`] as string | undefined;
    if (url && iconUrl && iconAlt) links.push({ url, iconUrl, iconAlt, ctaId });
  }
  return links;
}

function collectActions(props: Record<string, unknown>): ActionItem[] {
  const items: ActionItem[] = [];
  for (let i = 1; i <= 2; i++) {
    const iconUrl = props[`action${i}IconUrl`] as string | undefined;
    const label = props[`action${i}Label`] as string | undefined;
    if (!label) continue;
    const iconAlt = props[`action${i}IconAlt`] as string | undefined;
    const onClick = props[`action${i}OnClick`] as (() => void) | undefined;
    const spriteY = props[`action${i}SpriteY`] as number | undefined;
    items.push({ iconUrl: iconUrl || '', iconAlt, label, onClick, spriteY, slot: i });
  }
  return items;
}

function collectColumns(props: Record<string, unknown>): LinkColumn[] {
  const columns: LinkColumn[] = [];
  for (let c = 1; c <= 5; c++) {
    const title = props[`col${c}Title`] as string | undefined;
    if (!title) continue;
    const links: { text: string; url: string; ctaId?: string }[] = [];
    for (let i = 1; i <= 6; i++) {
      const text = props[`col${c}Link${i}Text`] as string | undefined;
      const url = props[`col${c}Link${i}Url`] as string | undefined;
      const ctaId = props[`col${c}Link${i}CtaId`] as string | undefined;
      if (text && url) links.push({ text, url, ctaId: ctaId || undefined });
    }
    columns.push({ title, links });
  }
  return columns;
}

/** Pattern matching component-specific props that should NOT be spread onto the DOM. */
const NON_HTML_PROP_PATTERN = /^(row[12]Link\d|row[12]AriaLabel|siteMapAriaLabel|socialAriaLabel|breadcrumbAriaLabel|social\d|action\d|col\d|topBar|logo|copyright|disclaimer|footnotes|breadcrumbs|languages|currentLanguage|languagePicker|countries|currentCountry|countryPicker|countrySpriteUrl|theme)/;

/**
 * PageFooter — backward-compatible flat-prop wrapper.
 *
 * Collects flat numbered props into structured arrays and delegates
 * all rendering to PageFooterNested.
 */
export const PageFooter: React.FC<PageFooterProps> = (props) => {
  const {
    theme,
    topBarText,
    topBarLinkText,
    topBarLinkUrl,
    topBarCtaId,
    logoImageUrl,
    logoAlt,
    logoUrl,
    logoCtaId,
    logoLdAppendix,
    copyrightText,
    disclaimerText,
    footnotesText,
    breadcrumbs: breadcrumbsJson,
    languages: languagesJson,
    currentLanguage,
    languagePickerHeading,
    languagePickerAddPathLocale,
    countries: countriesJson,
    currentCountry,
    countryPickerHeading,
    countryPickerFootnote,
    countrySpriteUrl,
    className,
    ...rest
  } = props;

  // Parse JSON string props into structured arrays
  const languages: FooterLanguage[] = useMemo(() => {
    try { return languagesJson ? JSON.parse(languagesJson) : []; } catch { return []; }
  }, [languagesJson]);

  const countries: FooterCountry[] = useMemo(() => {
    try { return countriesJson ? JSON.parse(countriesJson) : []; } catch { return []; }
  }, [countriesJson]);

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    try { return breadcrumbsJson ? JSON.parse(breadcrumbsJson) : []; } catch { return []; }
  }, [breadcrumbsJson]);

  // Collect flat numbered props into structured arrays
  const row1Links = collectLinks(props as Record<string, unknown>, 1);
  const row2Links = collectLinks(props as Record<string, unknown>, 2);
  const socialLinks = collectSocialLinks(props as Record<string, unknown>);
  const actions = collectActions(props as Record<string, unknown>);
  const columns = collectColumns(props as Record<string, unknown>);

  // Filter out component-specific props, pass only valid HTML attributes
  const domProps = Object.fromEntries(
    Object.entries(rest).filter(([key]) => !NON_HTML_PROP_PATTERN.test(key))
  );

  return (
    <PageFooterNested
      theme={theme}
      topBarText={topBarText}
      topBarLinkText={topBarLinkText}
      topBarLinkUrl={topBarLinkUrl}
      topBarCtaId={topBarCtaId}
      logoImageUrl={logoImageUrl}
      logoAlt={logoAlt}
      logoUrl={logoUrl}
      logoCtaId={logoCtaId}
      logoLdAppendix={logoLdAppendix}
      copyrightText={copyrightText}
      disclaimerText={disclaimerText}
      footnotesText={footnotesText}
      row1AriaLabel={props.row1AriaLabel}
      row2AriaLabel={props.row2AriaLabel}
      siteMapAriaLabel={props.siteMapAriaLabel}
      socialAriaLabel={props.socialAriaLabel}
      breadcrumbAriaLabel={props.breadcrumbAriaLabel}
      currentLanguage={currentLanguage}
      languagePickerHeading={languagePickerHeading}
      languagePickerAddPathLocale={languagePickerAddPathLocale}
      currentCountry={currentCountry}
      countryPickerHeading={countryPickerHeading}
      countryPickerFootnote={countryPickerFootnote}
      countrySpriteUrl={countrySpriteUrl}
      row1Links={row1Links}
      row2Links={row2Links}
      socialLinks={socialLinks}
      columns={columns}
      actions={actions}
      breadcrumbs={breadcrumbs}
      languages={languages}
      countries={countries}
      className={className}
      {...domProps}
    />
  );
};
